/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getConfigurationData } from './store/data';
import { getConfigurationMarketProperty, setWidgetDynamicHeight } from 'CoreUtils/WidgetUtils';
import { findDealers } from './store/search';
import { getCurrentPosition } from 'CoreStore/geolocation';
import { setInitialViewMode } from './store/viewMode';
import { setWidgetSettings } from './store/settings';
import AxiosConfiguration from 'CoreConsumers/AxiosConfiguration';
import SearchResultsViewMode from './components/SearchResults/SearchResultsViewMode';
import WidgetSearch from './components/WidgetSearch/WidgetSearch';
import WidgetSettings from 'CoreUtils/WidgetSettings';
import WidgetSettingsKeys from './constants/WidgetSettingsKeys';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';
import type { $BaseWidgetState } from 'CoreTypes/BaseWidgetState';
import type { $Configuration } from 'CoreTypes/Configuration';
import type { $WidgetSettings } from './types/WidgetSettings';
import type { $StoreState } from './types/StoreState';
import './Widget.scss';

type $ComponentProps = {
  settings: $WidgetSettings
};

type $MappedProps = {
  configuration: $Configuration,
  getConfigurationData: Function,
  findDealers: Function,
  getCurrentPosition: Function,
  setWidgetSettings: Function,
  setInitialViewMode: Function
};

export type $WidgetProps = $ComponentProps & $MappedProps;

class WidgetBase extends Component<$WidgetProps, $BaseWidgetState> {

  state = {
    widgetFields: [],
    settings: {}
  };

  static getDerivedStateFromProps(nextProps: $WidgetProps, cState: $BaseWidgetState) {
    const {
      configId: prevConfigId,
      market: prevMarket,
      language: prevLanguage
    } = cState.settings;

    const dswSettings = new WidgetSettings(WidgetSettingsKeys, nextProps.settings);
    const appliedSettings: $WidgetSettings = dswSettings.getWidgetSettings();

    if (JSON.stringify(cState.settings) !== JSON.stringify(appliedSettings)) {
      nextProps.setWidgetSettings(appliedSettings);
    }

    const { apiKey, configId, market, language } = appliedSettings;

    if (apiKey) AxiosConfiguration.setRequestAPIKey(apiKey);

    if ((configId !== prevConfigId || market !== prevMarket || language !== prevLanguage) &&
      configId && market && language) {
      nextProps.getConfigurationData(appliedSettings, configId, language, market);
    }

    const resultsViewMode = getConfigurationMarketProperty(
      nextProps.configuration, market || '',
      TemplateMarketPropertyKeys.DSW_SEARCH_RESULTS_VIEW_MODE
    );

    if (resultsViewMode) nextProps.setInitialViewMode(resultsViewMode);

    /* DL-1433 */
    if (resultsViewMode &&
      (resultsViewMode === SearchResultsViewMode.MAP_MODE.key ||
        resultsViewMode === SearchResultsViewMode.COMBO_MODE_MAP.key)) {
      const { configuration } = nextProps;
      const currentMarketProperties = market && configuration.marketProperties &&
        configuration.marketProperties[market];
      const shouldAskForGeolocationOnStart = !!(currentMarketProperties &&
        currentMarketProperties[TemplateMarketPropertyKeys.GEN_GEOLOCATION_ON_START]);

      nextProps.findDealers({})
        .then(() => shouldAskForGeolocationOnStart && nextProps.getCurrentPosition())
        .then(({error}) => {
          if(error) return;
          if(shouldAskForGeolocationOnStart) nextProps.findDealers({
            spellCheck: false,
            forceGeolocation: true,
          })
        })
        .catch(() => { /* Nothing to do here */ });
    }

    return {
      settings: appliedSettings
    };
  }

  render() {
    const { height } = this.state.settings;
    const style = setWidgetDynamicHeight(height);

    const rtl = !!(this.state.configuration && this.state.configuration.languages &&
      this.state.configuration.languages[0] && this.state.configuration.languages[0].rtl)

    return (
      <div dir={rtl ? "rtl" : "ltr"} className="dl-dsw dl-dsw--container" style={style}>
        <WidgetSearch />
      </div>
    );
  }
}

const mapStateToProps = (state: $StoreState) => ({
  configuration: state.dsw.configuration
});

const mapDispatchToProps: Object = {
  getConfigurationData,
  findDealers,
  getCurrentPosition,
  setWidgetSettings,
  setInitialViewMode
};

export default connect(mapStateToProps, mapDispatchToProps)(WidgetBase);
